/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { Usages } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { SearchManagementClient } from "../searchManagementClient.js";
import {
  QuotaUsageResult,
  UsagesListBySubscriptionNextOptionalParams,
  UsagesListBySubscriptionOptionalParams,
  UsagesListBySubscriptionResponse,
  UsagesListBySubscriptionNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing Usages operations. */
export class UsagesImpl implements Usages {
  private readonly client: SearchManagementClient;

  /**
   * Initialize a new instance of the class Usages class.
   * @param client Reference to the service client
   */
  constructor(client: SearchManagementClient) {
    this.client = client;
  }

  /**
   * Get a list of all Azure AI Search quota usages across the subscription.
   * @param location The unique location name for a Microsoft Azure geographic region.
   * @param options The options parameters.
   */
  public listBySubscription(
    location: string,
    options?: UsagesListBySubscriptionOptionalParams,
  ): PagedAsyncIterableIterator<QuotaUsageResult> {
    const iter = this.listBySubscriptionPagingAll(location, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listBySubscriptionPagingPage(location, options, settings);
      },
    };
  }

  private async *listBySubscriptionPagingPage(
    location: string,
    options?: UsagesListBySubscriptionOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<QuotaUsageResult[]> {
    let result: UsagesListBySubscriptionResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listBySubscription(location, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listBySubscriptionNext(location, continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listBySubscriptionPagingAll(
    location: string,
    options?: UsagesListBySubscriptionOptionalParams,
  ): AsyncIterableIterator<QuotaUsageResult> {
    for await (const page of this.listBySubscriptionPagingPage(location, options)) {
      yield* page;
    }
  }

  /**
   * Get a list of all Azure AI Search quota usages across the subscription.
   * @param location The unique location name for a Microsoft Azure geographic region.
   * @param options The options parameters.
   */
  private _listBySubscription(
    location: string,
    options?: UsagesListBySubscriptionOptionalParams,
  ): Promise<UsagesListBySubscriptionResponse> {
    return this.client.sendOperationRequest({ location, options }, listBySubscriptionOperationSpec);
  }

  /**
   * ListBySubscriptionNext
   * @param location The unique location name for a Microsoft Azure geographic region.
   * @param nextLink The nextLink from the previous successful call to the ListBySubscription method.
   * @param options The options parameters.
   */
  private _listBySubscriptionNext(
    location: string,
    nextLink: string,
    options?: UsagesListBySubscriptionNextOptionalParams,
  ): Promise<UsagesListBySubscriptionNextResponse> {
    return this.client.sendOperationRequest(
      { location, nextLink, options },
      listBySubscriptionNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listBySubscriptionOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Search/locations/{location}/usages",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.QuotaUsagesListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.$host, Parameters.subscriptionId, Parameters.location],
  headerParameters: [Parameters.accept, Parameters.clientRequestId],
  serializer,
};
const listBySubscriptionNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.QuotaUsagesListResult,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.location,
  ],
  headerParameters: [Parameters.accept, Parameters.clientRequestId],
  serializer,
};
